import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { addUnit, fetchUnitById, updateUnit } from "slices/Masters/unit/thunk";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import * as Yup from "yup";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const UOMForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const dispatch = useDispatch<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    unitName: "",
    unitSymbol: "",
  });

  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchUnitById(id))
        .unwrap()
        .then((res: any) => {
          setInitialData({
            unitName: res.unit_name,
            unitSymbol: res.unit_symbol,
          });
        })
        .catch((error: any) => {
          toast.error("Failed to load unit details");
          console.error("Fetch unit error:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [dispatch, id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      unitName: initialData.unitName || "",
      unitSymbol: initialData.unitSymbol || "",
    },
    validationSchema: Yup.object({
      unitName: Yup.string().trim().required("Please enter unit name"),
      // .matches(
      //   /^[A-Za-z\s-]+$/,
      //   "Unit name must contain only letters, spaces, and hyphens"
      // ),
      unitSymbol: Yup.string().trim().required("Please enter unit symbol"),
      // .matches(
      //   /^[A-Za-z ]+$/,
      //   "Unit Symbol must contain only letters and spaces"
      // ),
    }),
    onSubmit: async (values) => {
      const payload = {
        unit_name: values.unitName.trim(),
        unit_symbol: values.unitSymbol.trim(),
      };

      setIsSubmitting(true);

      try {
        if (isEdit) {
          await dispatch(updateUnit({ id, ...payload })).unwrap();
          toast.success("Unit updated successfully");
        } else {
          await dispatch(addUnit(payload)).unwrap();
          clearTableState(TableKeys.MASTER.UNIT);
          toast.success("Unit added successfully");
        }
        navigate("/master/uom");
      } catch (error: any) {
        // console.error("Unit operation failed:", error);
        toast.error(error?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Unit" : "Add Unit"}
        titleLink="/master/uom"
        breadcrumbs={[
          { label: "Unit", to: "/master/uom" },
          { label: id ? "Edit Unit" : "Add Unit", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />

      {isEdit && isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Unit Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="unitName"
                placeholder="Enter Unit Name"
                value={formik.values.unitName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.unitName && formik.touched.unitName}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.unitName}
              </Form.Control.Feedback>
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Unit Symbol <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="unitSymbol"
                placeholder="Enter Unit Symbol"
                value={formik.values.unitSymbol}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.unitSymbol && formik.touched.unitSymbol
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.unitSymbol}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton redirectTo="/master/uom" disabled={isSubmitting} />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default UOMForm;
